import { createContext } from 'svelte'
import { HttpApiClient, type ApiClient, type ApiClientOptions } from '@tadpoles/api-client'

import { createAppContext, type AppContext } from '../context/app-context.svelte.js'
import { createFamilyApiClientInfo } from './app-identity.js'
import { AccountDeletionService } from './account-deletion.svelte.js'
import { AuthenticationService } from './authentication/authentication.svelte.js'
import { BadgeService } from './badge.js'
import { AccountLanguageService } from './account-language.js'
import type { ConnectivityService } from './connectivity/service.js'
import { FamilyLocaleService } from './family-locale.svelte.js'
import { LibraryService, type LibraryServiceOptions } from './library/service.svelte.js'
import { ParametersService } from './parameters.js'
import { FamilyPendoService, type FamilyPendoServiceOptions } from './pendo.svelte.js'
import { PushNotificationsService } from './push-notifications/service.js'
import { SecurityContextService } from './security-context.js'
import {
    createLocalPersistencePolicy,
    type LocalPersistencePolicy
} from './persistence/storage-policy.js'
import { createFamilyPersistence } from './persistence/family-persistence.js'
import {
    createFamilyLocalBacking,
    type FamilyLocalBacking
} from './persistence/family-local-backing.js'
import { OrderedServiceLifecycle, type ServiceLifecycle } from './service-lifecycle.js'
import { UserScopedServicesManager } from './user-scoped-services-manager.svelte.js'

export interface AppServices {
    accountDeletion: AccountDeletionService
    apiClient: ApiClient
    accountLanguage: AccountLanguageService
    auth: AuthenticationService
    badge: BadgeService
    connectivity: ConnectivityService | undefined
    lifecycle: ServiceLifecycle
    library: LibraryService
    locale: FamilyLocaleService
    pendo: FamilyPendoService
    pushNotifications: PushNotificationsService
    securityContext: SecurityContextService
    localPersistencePolicy: LocalPersistencePolicy
    userScopedServicesManager: UserScopedServicesManager
}

export type AppServicesOptions = {
    appContext?: AppContext
    apiClient?: ApiClient
    apiClientOptions?: Omit<ApiClientOptions, 'baseUrl' | 'getClientInfo'>
    apiBaseUrl?: string
    connectivity?: ConnectivityService
    localBacking?: FamilyLocalBacking
    library?: Omit<LibraryServiceOptions, 'apiClient' | 'connectivity' | 'currentUserServices'>
    pendo?: FamilyPendoServiceOptions
    securityContext?: SecurityContextService
    localPersistencePolicy?: LocalPersistencePolicy
}

function getDefaultApiBaseUrl(): string {
    if (typeof location === 'undefined') {
        throw new Error('apiBaseUrl is required outside the browser.')
    }

    return location.origin
}

function isSessionAuthoritativeUnauthorizedPath(path: string): boolean {
    return !path.startsWith('/auth/')
}

export function createAppServices(options: AppServicesOptions = {}): AppServices {
    const appContext = options.appContext ?? createAppContext()
    const securityContext = options.securityContext ?? new SecurityContextService()
    const localPersistencePolicy =
        options.localPersistencePolicy ?? createLocalPersistencePolicy(securityContext.current)
    const persistence = createFamilyPersistence()
    const localBacking =
        options.localBacking ??
        createFamilyLocalBacking({
            attachmentCache:
                typeof caches !== 'undefined' && typeof location !== 'undefined'
                    ? {
                          cacheStorage: caches,
                          requestPersistentStorage:
                              typeof navigator !== 'undefined' && navigator.storage?.persist
                                  ? async () => await navigator.storage.persist()
                                  : undefined
                      }
                    : undefined,
            localPersistencePolicy,
            persistence
        })
    const apiClient = options.apiClient
        ? options.apiClient
        : new HttpApiClient({
              baseUrl: options.apiBaseUrl ?? getDefaultApiBaseUrl(),
              ...options.apiClientOptions,
              getClientInfo: createFamilyApiClientInfo,
              onUnauthorized: async (details) => {
                  try {
                      await options.apiClientOptions?.onUnauthorized?.(details)
                  } finally {
                      if (isSessionAuthoritativeUnauthorizedPath(details.path)) {
                          await auth.expireSession()
                      }
                  }
              }
          })
    const auth = new AuthenticationService(apiClient, localBacking)
    const parameters = new ParametersService(apiClient)
    const locale = new FamilyLocaleService()
    const pushNotifications = new PushNotificationsService()
    const userScopedServicesManager = new UserScopedServicesManager({
        apiClient,
        appContext,
        auth,
        connectivity: options.connectivity,
        localBacking,
        locale,
        parameters,
        pushNotifications
    })
    const pendo = new FamilyPendoService(auth, appContext, options.pendo)
    const accountDeletion = new AccountDeletionService({
        apiClient,
        auth,
        runtimeKind: securityContext.current.runtimeKind,
        userScopedServicesManager
    })
    const library = new LibraryService({
        ...options.library,
        apiClient,
        catalogBacking: options.library?.catalogBacking ?? localBacking.getLibraryCatalogBacking(),
        connectivity: options.connectivity,
        currentUserServices: userScopedServicesManager
    })
    const lifecycle = new OrderedServiceLifecycle([
        ...(options.connectivity === undefined ? [] : [options.connectivity]),
        userScopedServicesManager,
        library
    ])

    return {
        accountDeletion,
        apiClient,
        accountLanguage: new AccountLanguageService(apiClient, auth, userScopedServicesManager),
        auth,
        badge: new BadgeService(),
        connectivity: options.connectivity,
        lifecycle,
        library,
        locale,
        pendo,
        pushNotifications,
        securityContext,
        localPersistencePolicy,
        userScopedServicesManager
    }
}

export const [getAppServices, setAppServices] = createContext<AppServices>()
